// De tijdstrook (Verkennen ronde 4, C1/C2): onder de kaart, altijd zichtbaar.
//
// Eén strook, twee vensters. Staafjes per maand tonen waar het bewijs zit
// vóórdat je kiest; het blauwe venster is de periode, het gestippelde het
// vergelijkvenster ("ervoor", te verslepen). Voorkeuzes als chips, afspelen
// schuift beide vensters door de tijd. Vervangt het periodemenu en het
// maandraster van het prototype: geen menu, geen raster, één gebaar.
//
// Vorm uit de dummy: witte kaart, rand #ececec, radius 12, Inter.

import { useEffect, useRef, useState } from "react";

import { color, radius, size, weight } from "../tokens";
import { maandKort, type Periode } from "../lib/verkennen";

const PRESETS: { naam: string; maanden: number | "jaar" | "vorig" | null }[] = [
  { naam: "3 mnd", maanden: 3 },
  { naam: "6 mnd", maanden: 6 },
  { naam: "12 mnd", maanden: 12 },
  { naam: "24 mnd", maanden: 24 },
  { naam: "dit jaar", maanden: "jaar" },
  { naam: "vorig jaar", maanden: "vorig" },
  { naam: "alles", maanden: null },
];

export function Tijdstrook({
  min,
  nu,
  perMaand,
  periode,
  vergelijk,
  vergelijkVenster,
  telling,
  onPeriode,
  onVergelijk,
  onVerschuif,
}: {
  /** eerste en nieuwste maand op de kaart (jaar × 12 + maand) */
  min: number;
  nu: number;
  /** bewijs per maand binnen de afbakening: index 0 = `min` */
  perMaand: readonly number[];
  periode: Periode | null;
  vergelijk: boolean;
  vergelijkVenster: Periode | null;
  /** nieuw / in beweging / stil in de periode tegenover het vergelijkvenster */
  telling: { nieuw: number; beweging: number; stil: number } | null;
  onPeriode: (p: Periode | null) => void;
  onVergelijk: (aan: boolean) => void;
  /** het gestippelde venster verslepen: verschuiving in maanden t.o.v. "ervoor" */
  onVerschuif: (maanden: number) => void;
}) {
  const strook = useRef<HTMLDivElement>(null);
  const [speelt, setSpeelt] = useState(false);
  const [sleep, setSleep] = useState<null | { wat: "van" | "tot" | "was" | "nieuw"; start: number; begin: Periode | null }>(null);
  const totaal = Math.max(1, nu - min + 1);
  const max = Math.max(1, ...perMaand);

  const xVanMaand = (m: number) => ((m - min) / totaal) * 100;
  const maandVanX = (px: number) => {
    const r = strook.current?.getBoundingClientRect();
    if (!r) return min;
    const f = Math.max(0, Math.min(1, (px - r.left) / r.width));
    return Math.round(min + f * totaal - 0.5);
  };

  // afspelen: het venster schuift van links naar rechts, één maand per tik
  useEffect(() => {
    if (!speelt) return;
    const lengte = periode ? periode.tot - periode.van + 1 : 12;
    let van = periode && periode.van > min ? periode.van : min;
    if (periode && periode.tot >= nu) van = min;
    onPeriode({ van, tot: van + lengte - 1 });
    const tik = setInterval(() => {
      van += 1;
      if (van + lengte - 1 > nu) {
        setSpeelt(false);
        return;
      }
      onPeriode({ van, tot: van + lengte - 1 });
    }, 320);
    return () => clearInterval(tik);
    // bewust niet op `periode`: dan herstart elke tik de lus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speelt]);

  const kiesPreset = (p: (typeof PRESETS)[number]) => {
    setSpeelt(false);
    if (p.maanden === null) return onPeriode(null);
    const jaarNu = Math.floor((nu - 1) / 12);
    if (p.maanden === "jaar") return onPeriode({ van: jaarNu * 12 + 1, tot: nu });
    if (p.maanden === "vorig") return onPeriode({ van: (jaarNu - 1) * 12 + 1, tot: (jaarNu - 1) * 12 + 12 });
    onPeriode({ van: nu - p.maanden + 1, tot: nu });
  };
  const presetAan = (p: (typeof PRESETS)[number]) => {
    if (p.maanden === null) return periode === null;
    if (!periode) return false;
    if (typeof p.maanden === "number") return periode.tot === nu && periode.van === nu - p.maanden + 1;
    const jaarNu = Math.floor((nu - 1) / 12);
    if (p.maanden === "jaar") return periode.van === jaarNu * 12 + 1 && periode.tot === nu;
    return periode.van === (jaarNu - 1) * 12 + 1 && periode.tot === (jaarNu - 1) * 12 + 12;
  };

  // slepen: op een handvat begint een sleep van 'van' of 'tot'; op het
  // gestippelde venster van 'was'; op een lege plek een nieuw venster.
  useEffect(() => {
    if (!sleep) return;
    const move = (ev: PointerEvent) => {
      const m = maandVanX(ev.clientX);
      if (sleep.wat === "nieuw") {
        const a = Math.min(sleep.start, m);
        const b = Math.max(sleep.start, m);
        onPeriode({ van: Math.max(min, a), tot: Math.min(nu, b) });
      } else if (sleep.wat === "van" && periode) {
        onPeriode({ van: Math.max(min, Math.min(m, periode.tot)), tot: periode.tot });
      } else if (sleep.wat === "tot" && periode) {
        onPeriode({ van: periode.van, tot: Math.min(nu, Math.max(m, periode.van)) });
      } else if (sleep.wat === "was" && periode && sleep.begin) {
        onVerschuif(m - sleep.start);
      }
    };
    const up = () => setSleep(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleep, periode]);

  const knop = (aan: boolean) => ({
    cursor: "pointer", fontSize: size.label, padding: "3px 10px", borderRadius: radius.pill,
    border: `1px solid ${aan ? color.ink : color.border}`, background: aan ? color.ink : color.surface,
    color: aan ? color.surface : color.body, fontFamily: "inherit",
  });

  const jaren: number[] = [];
  for (let j = Math.floor((min - 1) / 12); j <= Math.floor((nu - 1) / 12); j++) jaren.push(j);

  return (
    <div
      data-tijdstrook
      style={{
        background: color.surface, border: `1px solid ${color.line}`, borderRadius: radius.card,
        padding: "10px 16px 8px", marginTop: 10,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {PRESETS.map((p) => (
          <button key={p.naam} type="button" onClick={() => kiesPreset(p)} style={knop(presetAan(p))}>
            {p.naam}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: size.label, color: color.soft }}>
          {periode ? `${maandKort(periode.van)} – ${maandKort(periode.tot)} · ${periode.tot - periode.van + 1} mnd` : `hele periode · ${maandKort(min)} – ${maandKort(nu)}`}
        </span>
        <button
          type="button"
          aria-label={speelt ? "Stop" : "Afspelen"}
          onClick={() => setSpeelt((s) => !s)}
          style={knop(speelt)}
        >
          {speelt ? "❚❚ stop" : "▶ afspelen"}
        </button>
        <button type="button" onClick={() => onVergelijk(!vergelijk)} style={knop(vergelijk)}>
          vergelijk {vergelijk ? "✓" : ""}
        </button>
      </div>

      <div
        ref={strook}
        style={{ position: "relative", height: 58, userSelect: "none", cursor: "crosshair" }}
        onPointerDown={(ev) => {
          if ((ev.target as HTMLElement).dataset.handvat) return;
          const m = maandVanX(ev.clientX);
          setSleep({ wat: "nieuw", start: m, begin: periode });
        }}
      >
        {/* staafjes */}
        <div style={{ position: "absolute", inset: "0 0 14px 0", display: "flex", alignItems: "flex-end", gap: 1 }}>
          {perMaand.map((n, i) => {
            const m = min + i;
            const inPer = periode ? m >= periode.van && m <= periode.tot : true;
            const inWas = vergelijk && vergelijkVenster ? m >= vergelijkVenster.van && m <= vergelijkVenster.tot : false;
            return (
              <div
                key={m}
                title={`${maandKort(m)}: ${n} punten met bewijs`}
                style={{
                  flex: 1, height: `${Math.max(2, (n / max) * 100)}%`, borderRadius: "1px 1px 0 0",
                  background: inPer ? "#2563eb" : inWas ? color.faint : color.disabled,
                  opacity: inPer ? 0.9 : inWas ? 0.8 : 0.45,
                }}
              />
            );
          })}
        </div>
        {/* vergelijkvenster (gestippeld, versleepbaar) */}
        {vergelijk && vergelijkVenster && (
          <div
            data-handvat="was"
            onPointerDown={(ev) => { ev.stopPropagation(); setSleep({ wat: "was", start: maandVanX(ev.clientX), begin: periode }); }}
            title="Sleep om met een andere periode te vergelijken"
            style={{
              position: "absolute", top: 0, bottom: 14, cursor: "grab",
              left: `${xVanMaand(vergelijkVenster.van)}%`,
              width: `${Math.max(0.5, xVanMaand(vergelijkVenster.tot + 1) - xVanMaand(vergelijkVenster.van))}%`,
              border: `2px dashed ${color.faint}`, borderRadius: 6,
            }}
          >
            <span style={{ position: "absolute", top: -1, left: 0, fontSize: 10, background: color.faint, color: color.surface, padding: "0 5px", borderRadius: 3, whiteSpace: "nowrap" }}>
              ervoor · {maandKort(vergelijkVenster.van)} – {maandKort(vergelijkVenster.tot)}
            </span>
          </div>
        )}
        {/* het venster met twee handvatten */}
        {periode && (
          <div
            style={{
              position: "absolute", top: 0, bottom: 14, pointerEvents: "none",
              left: `${xVanMaand(periode.van)}%`,
              width: `${Math.max(0.5, xVanMaand(periode.tot + 1) - xVanMaand(periode.van))}%`,
              border: "2px solid #2563eb", borderRadius: 6, background: "rgba(37,99,235,0.06)",
            }}
          >
            <span style={{ position: "absolute", top: -1, left: 0, fontSize: 10, background: "#2563eb", color: "#fff", padding: "0 5px", borderRadius: 3, whiteSpace: "nowrap" }}>
              {vergelijk ? "nu · " : ""}{maandKort(periode.van)} – {maandKort(periode.tot)}
            </span>
            {(["van", "tot"] as const).map((wat) => (
              <span
                key={wat}
                data-handvat={wat}
                onPointerDown={(ev) => { ev.stopPropagation(); setSleep({ wat, start: maandVanX(ev.clientX), begin: periode }); }}
                aria-label={wat === "van" ? "Beginmaand" : "Eindmaand"}
                style={{
                  position: "absolute", top: "50%", marginTop: -11, width: 10, height: 22, borderRadius: 3,
                  background: "#2563eb", cursor: "ew-resize", pointerEvents: "auto",
                  ...(wat === "van" ? { left: -6 } : { right: -6 }),
                }}
              />
            ))}
          </div>
        )}
        {/* jaartallen */}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 12, fontSize: 10, color: color.faint }}>
          {jaren.map((j) => (
            <span key={j} style={{ position: "absolute", left: `${xVanMaand(j * 12 + 1)}%` }}>{j}</span>
          ))}
        </div>
      </div>

      {vergelijk && telling && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {[
            ["nieuw", telling.nieuw, "#dc2626", "eerste bewijs in “nu”"],
            ["in beweging", telling.beweging, "#2563eb", "bewijs in beide"],
            ["stil", telling.stil, color.soft, "alleen bewijs ervoor"],
          ].map(([naam, n, kl, uitleg]) => (
            <span key={String(naam)} style={{ fontSize: size.label, color: color.soft, display: "inline-flex", gap: 6, alignItems: "baseline" }}>
              <b style={{ fontSize: 16, fontWeight: weight.medium, color: String(kl) }}>{String(n)}</b>
              {naam}: {uitleg}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
